import {
  Box,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ButtonZoom } from "../../../v2/utils/ButtonZoom";
import React, { useContext, useState } from "react";
import { PopUpDialog } from "../../../v2/utils/PopUpDialog";
import {
  FetchClassStudents,
  formatDate,
  generateGtmEvent,
  HandleDataLayer,
  ReSendLoginCredentialsToClass,
} from "../../../../Helpers";
import { SendClassCredentialsModal } from "../../../v2/userManagment/utils/SendClassCredentialsModal";
import { ErrorToast, SuccessToast } from "../../../v2/utils/Toast";
import { ClassExtended, UniversalModalCtx } from "../../../../interfaces";
import { UniversalModalContext } from "../../../../context/UniversalModalContext";
import { styled } from "@mui/material/styles";

interface ClassLoginCredentialsProps {
  classDetails: ClassExtended;
}

const useStyle = makeStyles((theme) => ({
  classCodeCard: {
    background: "#FFFFFF 0% 0% no-repeat padding-box",
    border: "2px solid #E8E8E8",
    borderRadius: "8px",
    padding: "24px",
    width: "auto",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
}));

export const SendLoginCredentialsCard = (props: ClassLoginCredentialsProps) => {
  const classes = useStyle();
  const { classDetails } = props;
  const [allUserActivatedModal, setAllUserActivatedModal] = useState(false);
  const [loginCredentialsPromptOpen, setLoginCredentialsPromptOpen] = useState(false);
  const [sendLoginCredentialsError, setSendLoginCredentialsError] = useState<
    undefined | string
  >(undefined);
  const [students, setStudents] = useState<any[]>([]);
  const universalModalCtx = useContext(UniversalModalContext) as UniversalModalCtx;
  const [failedToSendToStudents, setFailedToSendToStudents] = useState<any[]>([]);
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      padding: 8,
    },
  }));

  const handleSendClassLoginCredentials = async (classe: ClassExtended) => {
    FetchClassStudents(classe?.id!).then((res) => {
      let students = res.filter((student: any) => student.last_access_at === null);
      setStudents(students);
      if (students.length > 0) setLoginCredentialsPromptOpen(true);
      else setAllUserActivatedModal(true);
    });
  };

  return (
    <>
      <Box className={classes.classCodeCard}>
        <Stack flexDirection="row" alignItems="center" gap={"20px"}>
          <i className={`fal fa-envelope fa-md`}></i>
          <Typography
            variant="body1"
            sx={{
              font: "normal normal 600 20px/27px proxima-soft !important",
              letterSpacing: "0.15px",
              color: "#191919",
            }}>
            Login credentials
          </Typography>
        </Stack>
        <Typography
          variant="body1"
          sx={{ font: "normal normal normal 14px/21px Roboto" }}>
          Send a welcome email with a temporary password to students who haven’t activated
          their accounts.
        </Typography>
        <ButtonZoom
          disableZoom={true}
          size={"small"}
          variant="outlined"
          onClick={() => {
            HandleDataLayer(
              "modal",
              "open",
              "click",
              "main content",
              "login credentials",
              "send email",
              "onclick"
            );
            handleSendClassLoginCredentials(classDetails);
          }}
          sx={{
            width: "123px",
            height: "34px",
            borderRadius: "8px",
            fontSize: "16px",
          }}>
          Send Email
        </ButtonZoom>
      </Box>

      <PopUpDialog
        ctaFooterVariant={"default"}
        promptOpen={allUserActivatedModal}
        handleClose={() => {
          setAllUserActivatedModal(false);
          generateGtmEvent({
            event: "modal",
            action: "close",
            name: "onclick",
            type: "click",
            region: "main content",
            section: "send login credentials",
            text: "close cross",
          });
        }}
        handleOkClick={async () => {
          setAllUserActivatedModal(false);
          generateGtmEvent({
            event: "link",
            action: "click",
            name: "onclick",
            type: "internal link",
            region: "main content",
            section: "send login credentials",
            text: "Ok",
          });
        }}
        handleCancelClick={() => {
          setAllUserActivatedModal(false);
          generateGtmEvent({
            event: "link",
            action: "click",
            name: "onclick",
            type: "internal link",
            region: "main content",
            section: "send login credentials",
            text: "cancel",
          });
        }}
        okButtonLabel={"Ok"}
        popupTitle={`Send login credentials`}
        cancelButtonLabel={undefined}>
        <Stack direction={"column"} spacing={4}>
          <>
            <Typography variant="subtitle2">
              Students in {classDetails?.course_name} {classDetails?.period_block}{" "}
              {classDetails?.start_date && formatDate(new Date(classDetails.start_date))}{" "}
              have already activated their accounts successfully.
            </Typography>
            <Typography variant={"body1"}>
              Please have them reset their password from the login page if they are having
              trouble logging in, or contact support if you need additional assistance.
            </Typography>
          </>
        </Stack>
      </PopUpDialog>
      <SendClassCredentialsModal
        ctaFooterVariant={"default"}
        promptOpen={loginCredentialsPromptOpen}
        handleClose={() => {
          setLoginCredentialsPromptOpen(false);
          setSendLoginCredentialsError(undefined);
          setStudents([]);
          generateGtmEvent({
            event: "modal",
            action: "close",
            name: "onclick",
            type: "click",
            region: "main content",
            section: sendLoginCredentialsError
              ? "Error sending login credentials"
              : "send login credentials",
            text: "close cross",
          });
        }}
        handleOkClick={async () => {
          setLoginCredentialsPromptOpen(false);
          setSendLoginCredentialsError(undefined);
          setStudents([]);
          if (!sendLoginCredentialsError) {
            universalModalCtx.setLoaderDialogOpen(true);
            universalModalCtx.setLoaderDialogText("Sending login credentials...");
            ReSendLoginCredentialsToClass(classDetails.id)
              .then((response) => {
                universalModalCtx.setLoaderDialogOpen(false);
                universalModalCtx.setLoaderDialogText("");
                if (response.status) {
                  SuccessToast("Login credentials sent successfully!");
                } else {
                  setLoginCredentialsPromptOpen(true);
                  setSendLoginCredentialsError("Error");
                  setFailedToSendToStudents(response.failedUsers);
                }
              })
              .catch((e) => {
                universalModalCtx.setLoaderDialogOpen(false);
                universalModalCtx.setLoaderDialogText("");
                console.log(e.message);
                ErrorToast(e.message);
              });
          }
          generateGtmEvent({
            event: "link",
            action: "click",
            name: "onclick",
            type: "internal link",
            region: "main content",
            section: sendLoginCredentialsError
              ? "Error sending login credentials"
              : "send login credentials",
            text: sendLoginCredentialsError ? "Ok" : "Send",
          });
        }}
        handleCancelClick={() => {
          setLoginCredentialsPromptOpen(false);
          setSendLoginCredentialsError(undefined);
          setStudents([]);
          generateGtmEvent({
            event: "link",
            action: "click",
            name: "onclick",
            type: "internal link",
            region: "main content",
            section: "send login credentials",
            text: "cancel",
          });
        }}
        okButtonLabel={
          sendLoginCredentialsError ? "Ok" : students.length > 0 ? "Send" : "Ok"
        }
        title={
          sendLoginCredentialsError
            ? `Error sending login credentials`
            : `Send login credentials`
        }
        cancelButtonLabel={!sendLoginCredentialsError ? "Cancel" : undefined}>
        <Stack direction={"column"} spacing={4} sx={{ padding: "0px 8px" }}>
          {!sendLoginCredentialsError ? (
            <>
              <Typography variant="body1">
                Confirm you would like to send a welcome email with a temporary password
                to{" "}
                <b>
                  {" "}
                  {classDetails?.course_name} {classDetails?.period_block}{" "}
                  {classDetails?.start_date &&
                    formatDate(new Date(classDetails.start_date))}{" "}
                </b>
                students who haven't activated their accounts.
              </Typography>
              <Divider />
              <Typography variant={"body1"}>
                The following students will receive the email:
              </Typography>
            </>
          ) : (
            <Typography variant="body1">
              There was an error sending the welcome email to the following students.
              Please try again later or contact digitsupport@asu.edu for assistance.
            </Typography>
          )}
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 700 }} aria-label="customized table">
              <TableBody>
                {!sendLoginCredentialsError
                  ? students.map((row) => {
                      if (row.last_access_at === null) {
                        return (
                          <StyledTableRow key={row.id}>
                            <StyledTableCell component="th" scope="row">
                              {row.full_name}
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      } else {
                        return <></>;
                      }
                    })
                  : failedToSendToStudents.map((row) => {
                      return (
                        <StyledTableRow key={row.id}>
                          <StyledTableCell component="th" scope="row">
                            {row.full_name}
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </SendClassCredentialsModal>
    </>
  );
};
